/**
 * Subscription and entitlements.
 *
 * 1. Access is decided from the explicit subscription status; an unrecognised
 *    status fails closed to DENIED — never granted.
 * 2. The wallet balance is shown as an unresolved blocker, not as a number.
 */
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import type { BalanceBlocker, Entitlements, Recipe, Subscription } from '../api/types';
import { Card, Empty, ErrorBox, Loading, StatePill, Tag } from '../components/Metric';

const DECISION_COPY: Record<string, string> = {
  GRANTED: 'Recipes are unlocked for this organisation.',
  AT_RISK:
    'Payment is outstanding. Inspection keeps running — a subscription problem mid-shift is not a reason to darken a production line — but access may be withdrawn. Settle the balance in the marketplace.',
  DENIED: 'Access is denied. Complete checkout in the marketplace to continue.',
  NO_SUBSCRIPTION: 'No subscription exists for this application yet.',
};

export function SubscriptionPage() {
  const sub = useApi<Subscription>(() => api.get<Subscription>('/api/v1/marketplace/subscription'), []);
  const ent = useApi<{ entitlements: Entitlements } | Entitlements>(
    () => api.get('/api/v1/marketplace/entitlements'),
    [],
  );
  const balance = useApi<{ blocker: BalanceBlocker; readings: unknown[] }>(
    () => api.get('/api/v1/marketplace/balance'),
    [],
  );
  const recipes = useApi<Recipe[]>(() => api.get<Recipe[]>('/api/v1/recipes'), []);

  if (sub.loading) return <Loading label="Loading subscription…" />;
  if (sub.error) return <ErrorBox message={sub.error} />;
  if (!sub.data) return null;

  const entitlements =
    ent.data && 'entitlements' in ent.data ? ent.data.entitlements : (ent.data as Entitlements);
  const entitledSlugs = new Set(entitlements?.recipeSlugs ?? []);
  const decision = sub.data.access.decision;

  return (
    <>
      <div className="grid grid-2">
        <Card
          title="Subscription status"
          actions={<StatePill state={decision} />}
          note={`Status reported by the marketplace: ${sub.data.status}. Access is decided from this explicit value — an unrecognised status is treated as denied, never granted.`}
        >
          <div className={`callout ${decision === 'GRANTED' ? 'info' : decision === 'AT_RISK' ? 'warn' : 'danger'}`} style={{ marginBottom: 12 }}>
            <strong>{DECISION_COPY[decision] ?? 'Access state unknown — treated as denied.'}</strong>
            {sub.data.access.reason}
          </div>
          <table className="kv">
            <tbody>
              <tr>
                <td>Marketplace status</td>
                <td className="mono">{sub.data.status}</td>
              </tr>
              <tr>
                <td>Price model</td>
                <td>{sub.data.priceModelName ?? 'None attached'}</td>
              </tr>
              <tr>
                <td>Local record</td>
                <td>{sub.data.local ? 'Present (provisioned via webhook)' : 'Absent'}</td>
              </tr>
            </tbody>
          </table>
          {sub.data.access.offerMarketplaceRedirect && (
            <a className="btn primary" style={{ marginTop: 14 }} href={sub.data.subscribeUrl}>
              Manage subscription in the marketplace
            </a>
          )}
        </Card>

        <Card
          title="Pricing principle"
          note="How this product is meant to be priced. Actual terms come from the marketplace price model attached to your subscription."
        >
          <table className="kv">
            <tbody>
              <tr>
                <td>Billable unit</td>
                <td>
                  <strong>per {sub.data.pricingPrinciple.billableUnit}</strong>
                </td>
              </tr>
              <tr>
                <td>Never billable</td>
                <td>per {sub.data.pricingPrinciple.neverBillableUnit}</td>
              </tr>
              <tr>
                <td>Model</td>
                <td className="mono">
                  {sub.data.pricingPrinciple.recommendedModelType} / {sub.data.pricingPrinciple.interval}
                </td>
              </tr>
              <tr>
                <td>Indicative annual</td>
                <td>
                  ${sub.data.pricingPrinciple.indicativeAnnualUsd[0]?.toLocaleString()}–$
                  {sub.data.pricingPrinciple.indicativeAnnualUsd[1]?.toLocaleString()} per machine
                </td>
              </tr>
            </tbody>
          </table>
          <div className="card-note">
            Adding cameras to a licensed machine does not consume another
            licence slot. Hardware is passed through at cost as CapEx.
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card
          title="Entitlements"
          actions={
            entitlements && (
              <span className="pill muted">
                plan: {entitlements.planKey}
                {entitlements.machineQuota != null
                  ? ` · ${entitlements.licensedMachineCount}/${entitlements.machineQuota} machines`
                  : ` · ${entitlements.licensedMachineCount} machines (no quota)`}
              </span>
            )
          }
          note="Plan contents are PlasticVision's own packaging decision and can be overridden per organisation by an administrator. They are not a Sensifai quotation."
        >
          {!entitlements ? (
            <Empty title="No entitlement data" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Recipe</th>
                    <th>Phase</th>
                    <th>Included</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recipes.data?.map((r) => {
                    const included = entitledSlugs.has(r.slug);
                    return (
                      <tr key={r.id}>
                        <td>
                          <strong>{r.name}</strong>
                          {!r.available && <div className="dim">coming soon</div>}
                        </td>
                        <td className="mono" style={{ fontSize: 11.5 }}>{r.rolloutPhase}</td>
                        <td>
                          {included ? (
                            <span className="pill ok">included</span>
                          ) : (
                            <span className="pill muted">not included</span>
                          )}
                        </td>
                        <td style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                          {!included && 'Upgrade or request an override'}
                          {included && !r.available && 'Available in a later phase'}
                          {included && r.available && 'Ready to bind to a station'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card
          title="Wallet balance"
          actions={
            balance.data?.blocker?.blocked ? (
              <span className="pill danger">Held — unresolved</span>
            ) : (
              <span className="pill ok">Available</span>
            )
          }
          note="Read this section before asking for a balance figure in a demo."
        >
          {balance.error && <ErrorBox message={balance.error} />}
          {!balance.data ? (
            <Loading />
          ) : balance.data.blocker.blocked ? (
            <>
              <div className="callout danger">
                <strong>No balance figure is shown, on purpose</strong>
                {balance.data.blocker.reason}
              </div>
              <table className="kv">
                <tbody>
                  <tr>
                    <td>Endpoint</td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {balance.data.blocker.documentedConflict.endpoint}
                    </td>
                  </tr>
                  <tr>
                    <td>{balance.data.blocker.documentedConflict.sourceA.doc}</td>
                    <td>{balance.data.blocker.documentedConflict.sourceA.claim}</td>
                  </tr>
                  <tr>
                    <td>{balance.data.blocker.documentedConflict.sourceB.doc}</td>
                    <td>{balance.data.blocker.documentedConflict.sourceB.claim}</td>
                  </tr>
                  <tr>
                    <td>Discrepancy</td>
                    <td>
                      <strong>{balance.data.blocker.documentedConflict.magnitude}</strong> on a
                      money field
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="card-note">
                The raw readings are stored and can be inspected, but the value
                is never used for an access decision and never rendered as a
                number until Sensifai confirm the unit in writing. <Tag>{balance.data.blocker.documentedConflict.magnitude} conflict</Tag>
              </div>
            </>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              Balance is confirmed and available for display.
            </p>
          )}
        </Card>
      </div>
    </>
  );
}
